import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { newIdempotencyKey } from '../client/idempotency';
import { ContentBase } from '../content/contracts';
/**
 * Case studies and their discussion threads (spec §7). Pro and TSP only.
 *
 * The design's three detail sections map to three contract fields exactly —
 * `context`, `intervention`, `outcome` — which is the closest design-to-contract
 * match in the product after the cohort.
 */
export const CaseStudy = ContentBase.extend({
    type: z.literal('CASE_STUDY'),
    title: z.string(),
    summary: z.string().optional(),
    /** The three paid sections — all null when `locked`. */
    context: z.string().nullable(),
    intervention: z.string().nullable(),
    outcome: z.string().nullable(),
    /**
     * How long this takes to read, twice over — and the two are different
     * questions rather than a fallback pair.
     *
     * `readingMinutes` is **entity-level and authored**: an editorial judgement
     * about the piece, how long a reader will sit with it. One value, the same
     * in every locale, exactly as `Reading.readingMinutes` is.
     *
     * `estimatedMinutes` is **derived per read, from the text actually
     * resolved** — so a French case and its longer English translation report
     * different figures. 200 words a minute, minimum one: any words at all round
     * up, because "0 min" reads as *empty* rather than *short*.
     *
     * I asked for one authored field and argued it should be per locale. The
     * backend's answer is better: the per-locale question was always about the
     * measurement, and a second authored column would be a number an editor has
     * to keep in step every time either text changes.
     *
     * Both are counted over all three sections and taken **before** the paywall
     * withholds them, so a locked case still says how long it is.
     */
    readingMinutes: z.number().int().nullable(),
    wordCount: z.number().int().nonnegative(),
    estimatedMinutes: z.number().int().nullable(),
    /**
     * The question the clinical team ends the case on — what the thread beneath
     * is answering, not a summary of what happened.
     *
     * Per locale, and **withheld behind the paywall** along with the three
     * sections: a dangling "For discussion: what would you change in the
     * ladder…" over a case somebody cannot read is worse than asking nothing.
     * `summary` stays; that is the teaser.
     */
    discussionPrompt: z.string().nullable(),
});
/**
 * The minutes to print on a card or a byline.
 *
 * The authored judgement wins when somebody made one; the measurement is the
 * floor so a case nobody has assessed still says how long it is. Written once
 * because the card and the detail must not disagree.
 */
export function caseMinutes(study) {
    return study.readingMinutes ?? study.estimatedMinutes;
}
export const ReactionKind = z.enum(['HEART', 'ANCHOR', 'HUG', 'LIGHTBULB']);
/**
 * Reaction counts — all four kinds, always, zero when nobody reacted.
 *
 * ── Why this is an exhaustive object and not a record ─────────────────────
 * This was `z.record(ReactionKind, …)`, which parses `{}` and every partial.
 * The backend closed that gap in round 7 and the argument was better than ours:
 * absence in this API means *"this route does not answer that question"* —
 * `MediaListItem.variants` is absent on a list row for exactly that reason —
 * and a reaction count is never in that state, because it comes off the same
 * query as the post. Only "nobody reacted" is reachable, and that is a zero.
 *
 * Their second point is the one that settles it: the two halves of their
 * contract disagreed. `z.record` infers `Partial<Record<…>>` in TypeScript
 * while `zod-to-json-schema` emitted all four as **required**, so only the
 * loose half was ever checked. Keeping the record here would have meant this
 * client carrying the same drift they had just removed.
 *
 * The consequence is deliberate: a server that reverts to `{}` fails at this
 * boundary rather than rendering silent zeros. They have committed to telling
 * us before that changes.
 */
export const Reactions = z.object({
    HEART: z.number().int().nonnegative(),
    ANCHOR: z.number().int().nonnegative(),
    HUG: z.number().int().nonnegative(),
    LIGHTBULB: z.number().int().nonnegative(),
});
/**
 * `photoUrl` (9 Oct 2026) is the author's own portrait, a signed derivative —
 * null when they have none, absent from a server that predates it. Never persist it.
 */
export const PostAuthor = z.object({
    id: z.string().uuid(),
    fullName: z.string(),
    photoUrl: z.string().nullish(),
});
/**
 * A member-uploaded image on a post or comment.
 *
 * ── You never receive the original ────────────────────────────────────────
 * `url` resolves to a **derivative**, re-encoded without metadata. A member
 * upload confirms with `url: null` and there is no route to the original — that
 * is the EXIF guarantee rather than a not-ready state, and it is why this
 * carries a `state` instead of just a nullable URL.
 *
 * `UNAVAILABLE` **never becomes `READY`** — the render failed permanently — so
 * it gets a broken-image affordance and not a spinner. There is deliberately no
 * fallback to the original: a render failure is when the bytes are least
 * understood, which is the worst moment to serve them raw.
 *
 * A video has a second way of landing there: a `video/mp4` may contain HEVC,
 * which most browsers will not play, and a remux cannot change a codec. Those
 * are refused on the bytes and recorded permanently, with `url` and `posterUrl`
 * both null. The composer probes playability before uploading so a member is
 * told in the box rather than on a live post — see `canPlayVideo`.
 *
 * `url` and `thumbnailUrl` are signed and short-lived. Same rule as every other
 * signed URL here: never persist them, never log them.
 */
export const PostAttachment = z.object({
    assetId: z.string().uuid(),
    /**
     * **Branch on this first.** A member may post a clip since 9 October, and
     * `url` means two different things depending on the answer.
     */
    kind: z.enum(['image', 'video']),
    state: z.enum(['PROCESSING', 'READY', 'UNAVAILABLE']),
    /**
     * For an image, the 1024px derivative. For a video, the **remux** — the same
     * pictures and sound, re-containerised with the metadata stripped and the
     * index moved to the front, so it starts without downloading the whole file.
     * Never the bytes the member uploaded, in either case. Non-null only when
     * `READY`.
     */
    url: z.string().nullable(),
    /** The 320px thumbnail. For a video it is the poster, same URL. */
    thumbnailUrl: z.string().nullable(),
    /** The still to show before playback. Null for an image. */
    posterUrl: z.string().nullable(),
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    /**
     * Read from the bytes. Null for an image, and null for a video whose length
     * could not be read — which is honest, and is why the badge renders nothing
     * rather than « 0:00 ».
     */
    durationSeconds: z.number().nonnegative().nullable(),
});
/**
 * A poll on a post or a reply (8 Oct 2026). One vote per member, changeable.
 *
 * `votes` is null until the caller may see results — after voting, once the
 * poll is closed, or to its author — so a member is not nudged by the count
 * before choosing. `totalVotes` is always there.
 */
export const PollOption = z.object({
    id: z.string().uuid(),
    label: z.string(),
    votes: z.number().int().nonnegative().nullable(),
});
export const PollView = z.object({
    id: z.string().uuid(),
    options: z.array(PollOption),
    totalVotes: z.number().int().nonnegative(),
    myVote: z.string().uuid().nullable(),
    closed: z.boolean(),
    canClose: z.boolean(),
});
export const Comment = z.object({
    id: z.string().uuid(),
    parentCommentId: z.string().uuid().nullable(),
    author: PostAuthor,
    body: z.string(),
    createdAt: z.string().datetime(),
    /** Always present, `[]` when none — same rule as `reactions`. */
    attachments: z.array(PostAttachment),
    /** Null without a poll; absent from a server that predates polls. */
    poll: PollView.nullish(),
    /**
     * Reactions on this reply (11 Oct 2026), the post's four kinds, all four at
     * zero when nobody reacted. Optional only because a server that predates them
     * sends neither field — read it through `commentReactions`.
     */
    reactions: Reactions.optional(),
    myReactions: z.array(ReactionKind).optional(),
});
const NO_REACTIONS = { HEART: 0, ANCHOR: 0, HUG: 0, LIGHTBULB: 0 };
/** A reply's counts and the caller's own kinds, zero and empty where a server sent none. */
export function commentReactions(comment) {
    return { reactions: comment.reactions ?? NO_REACTIONS, mine: comment.myReactions ?? [] };
}
export const Post = z.object({
    id: z.string().uuid(),
    roomId: z.string().uuid().nullable(),
    contentItemId: z.string().uuid().nullable(),
    author: PostAuthor,
    body: z.string(),
    pinned: z.boolean(),
    createdAt: z.string().datetime(),
    /**
     * When the text was last edited — by its author, or by a moderator of the
     * room (29 Sep 2026). Null for a post never edited; absent from a server
     * that predates the field, which reads the same way.
     */
    editedAt: z.string().datetime().nullish(),
    /** Counts per kind, and which ones the caller left. */
    reactions: Reactions,
    myReactions: z.array(ReactionKind),
    bookmarked: z.boolean(),
    comments: z.array(Comment),
    commentCount: z.number().int().nonnegative(),
    /** Always present, `[]` when none — never absent. */
    attachments: z.array(PostAttachment),
    poll: PollView.nullish(),
});
/** The `pagedPlain` envelope — no `locale`, unlike the content lists. */
const pagedPlain = (item) => z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/**
 * A case study's thread.
 *
 * The backend fixed the access check on this route after we reported it: it
 * previously accepted any content item id with no audience, entitlement or
 * status check, and returned 201 on a write. It now answers the type's own
 * `*_NOT_FOUND` for anything the caller may not read — so a failure here is
 * indistinguishable from a missing id, which is the point.
 */
export function useCaseStudyPosts(http, id) {
    return useQuery({
        queryKey: ['community', 'case-studies', id, 'posts'],
        enabled: Boolean(id),
        queryFn: () => http.get(`/community/case-studies/${id}/posts`, { schema: pagedPlain(Post) }),
    });
}
export function useCreateCaseStudyPost(http, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ body, attachmentIds }) => http.post(`/community/case-studies/${id}/posts`, {
            body,
            // Omitted rather than sent empty: the contract defaults it to `[]`, and
            // an empty array on the wire says "I considered attachments" where
            // absence says nothing at all.
            ...(attachmentIds?.length ? { attachmentIds } : {}),
        }, {
            schema: Post,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['community', 'case-studies', id, 'posts'] }),
    });
}
/**
 * Author-delete. **Hard, and it deletes the bytes.**
 *
 * Comments, replies, reactions and bookmarks go with the post, and every
 * attached image nothing else shows is removed from storage — a signed URL
 * somebody already loaded stops resolving. There is no tombstone, because a
 * tombstone would have left the photograph readable to anyone holding a link,
 * which is the thing being asked to end.
 *
 * Somebody else's post is **403 `POST_NOT_YOURS`, not 404** — the opposite of
 * the rule everywhere else here, and right: you can plainly see the post, so a
 * false 404 would be the worse answer.
 *
 * The author's own removal route — and, since 29 Sep 2026, a moderator's of
 * the room too (staff, or a TSP facilitator on their cohort's rooms).
 */
export function useDeletePost(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (postId) => http.post(`/community/posts/${postId}/delete`, {}, {
            schema: z.object({ ok: z.boolean() }).passthrough(),
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/**
 * Moderating a room from the member app — TSP request 9 (29 Sep 2026).
 *
 * `Room.canModerate` is true for staff and for a TSP facilitator on their own
 * cohort's rooms; the server checks it again on every one of these (403
 * `ROOM_MODERATION_REQUIRED`). Hiding is reversible by the La Boussole team
 * from the back office; the post leaves every member's view at once.
 */
export function usePinPost(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ postId, on }) => http.post(`/community/posts/${postId}/pin`, { on }, { schema: Post }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
export function useHidePost(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (postId) => http.post(`/community/posts/${postId}/hide`, {}, { schema: z.object({ id: z.string() }).passthrough() }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/** Edit a post's text: the author their own, a moderator of the room any post in it. */
export function useEditPost(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ postId, body }) => http.post(`/community/posts/${postId}/update`, { body }, { schema: Post }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
export function useDeleteComment(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (commentId) => http.post(`/community/comments/${commentId}/delete`, {}, {
            schema: z.object({ ok: z.boolean() }).passthrough(),
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
/**
 * React, and un-react.
 *
 * The same route reverses it with `{ on: false }` — there is no `unreact`, and
 * the backend wrote that down as a contract because the back office read the
 * pin route as one-way and asked for a route that already existed.
 */
/** React to a reply, or take it back (11 Oct 2026). The whole post comes back. */
export function useToggleCommentReaction(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ commentId, kind, on }) => http.post(`/community/comments/${commentId}/react`, { kind, on }, { schema: Post }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
export function useToggleReaction(http, invalidate) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ postId, kind, on }) => http.post(`/community/posts/${postId}/react`, { kind, on }, {
            schema: Post,
            idempotencyKey: newIdempotencyKey(),
        }),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: invalidate }),
    });
}
//# sourceMappingURL=cases.js.map